import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'AI App', href: '#ai-app' },
    { name: 'AI Doctor', href: '#ai-doctor' },
  ];

  return (
    <motion.header 
      className="bg-dark-900/90 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-primary-900/20"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" aria-label="Top">
        <div className="flex w-full items-center justify-between border-b border-gray-200 py-6 lg:border-none">
          <div className="flex items-center">
            <motion.a 
              href="/" 
              className="flex items-center text-2xl font-bold text-primary-500"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <img 
                src="/logo.png" 
                alt="Speedr Logo" 
                className="h-8 w-auto mr-2"
              />
              Speedr
            </motion.a>
          </div>
          <div className="ml-10 hidden space-x-8 lg:block">
            {navigation.map((link, index) => {
              const isExternalLink = link.href.startsWith('/');
              
              return isExternalLink ? (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="inline-block"
                >
                  <Link
                    to={link.href}
                    className="text-base font-medium text-primary-200 hover:text-primary-400 relative group inline-block"
                  >
                    {link.name}
                    <motion.span 
                      className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary-500 group-hover:w-full transition-all duration-300"
                      initial={{ width: 0 }}
                      whileHover={{ width: '100%' }}
                    />
                  </Link>
                </motion.div>
              ) : (
                <motion.a
                  key={link.name}
                  href={link.href}
                  className="text-base font-medium text-primary-200 hover:text-primary-400 relative group inline-block"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ y: -2 }}
                >
                  {link.name}
                  <motion.span 
                    className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary-500 group-hover:w-full transition-all duration-300"
                    initial={{ width: 0 }}
                    whileHover={{ width: '100%' }}
                  />
                </motion.a>
              );
            })}
          </div>

          {/* Header Action Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.6 }}
            >
              <Link
                to="/privacy"
                className="px-4 py-2 text-sm font-medium text-primary-200 hover:text-primary-400 border border-primary-500/30 rounded-lg hover:bg-primary-500/10 transition-all duration-300"
              >
                Policy
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              <Link
                to="/contact"
                className="px-4 py-2 text-sm font-medium text-primary-200 hover:text-primary-400 border border-primary-500/30 rounded-lg hover:bg-primary-500/10 transition-all duration-300"
              >
                Contact Us
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.8 }}
            >
              <Link
                to="/press"
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-all duration-300"
              >
                Press
              </Link>
            </motion.div>
          </div>
          <div className="lg:hidden">
            <motion.button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-primary-200 hover:bg-primary-900/20"
              onClick={() => setMobileMenuOpen(true)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </motion.button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <motion.div 
          className="lg:hidden"
          initial={{ opacity: 0, x: 300 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 300 }}
          transition={{ duration: 0.3 }}
        >
          <div className="fixed inset-0 z-50" />
          <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-dark-900 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-primary-800/50">
            <div className="flex items-center justify-between">
              <motion.a 
                href="/" 
                className="flex items-center -m-1.5 p-1.5"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <img 
                  src="/logo.png" 
                  alt="Speedr Logo" 
                  className="h-8 w-auto mr-2"
                />
                <span className="text-2xl font-bold text-primary-500">Speedr</span>
              </motion.a>
              <motion.button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-primary-200 hover:bg-primary-900/20"
                onClick={() => setMobileMenuOpen(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </motion.button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {navigation.map((item, index) => {
                    const isExternalLink = item.href.startsWith('/');
                    
                    return isExternalLink ? (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <Link
                          to={item.href}
                          className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-primary-100 hover:bg-primary-900/20"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      </motion.div>
                    ) : (
                      <motion.a
                        key={item.name}
                        href={item.href}
                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-primary-100 hover:bg-primary-900/20"
                        onClick={() => setMobileMenuOpen(false)}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ x: 5 }}
                      >
                        {item.name}
                      </motion.a>
                    );
                  })}
                  
                  {/* Mobile Action Buttons */}
                  <div className="pt-4 mt-4 border-t border-primary-900/30 space-y-2">
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.4 }}
                    >
                      <Link
                        to="/privacy"
                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-primary-100 hover:bg-primary-900/20"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Policy
                      </Link>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                    >
                      <Link
                        to="/contact"
                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-primary-100 hover:bg-primary-900/20"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Contact Us
                      </Link>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.6 }}
                    >
                      <Link
                        to="/press"
                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-primary-100 bg-primary-600 hover:bg-primary-700"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Press
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Navigation;
